import { useEffect, useRef, useState } from 'react';
import type { Me, PaymentProviderName, ProductPublic } from '@tarot/contracts';
import { http } from '@/api/client';
import { useMe, useSetMe, useShop } from '@/api/hooks';
import { useUi } from '@/app/store';
import { Sheet } from '@/components/Sheet';
import { platform } from '@/platform';
import { Icon } from '@/components/Icon';

export type PaywallReason = 'premium' | 'append' | 'funds';

interface Props { open: boolean; reason: PaywallReason; crystalsOnly?: boolean; onClose: (purchased: boolean) => void }

const TITLES: Record<PaywallReason, string> = { premium: 'На сегодня расклады закончились', append: 'Дополнить расклад', funds: 'Недостаточно кристаллов' };

/**
 * Пейвол: Premium + пакеты кристаллов.
 * Провайдер: Robokassa (рубли), если сервер его сообщает; иначе Telegram Stars.
 * Robokassa открывается во внешнем браузере; после оплаты сервер получает уведомление,
 * а клиент опрашивает /me, пока баланс/подписка не изменятся.
 */
export function Paywall({ open, reason, crystalsOnly, onClose }: Props) {
  const { data: me } = useMe();
  const { data: shop } = useShop();
  const setMe = useSetMe();
  const toast = useUi((s) => s.showToast);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState<ProductPublic | null>(null);
  const [purchased, setPurchased] = useState(false);
  const pollTimer = useRef<number | null>(null);

  const provider: PaymentProviderName = (shop?.providers ?? []).includes('robokassa') ? 'robokassa' : 'stars';
  const premium = shop?.products.find((p) => p.type === 'premium');
  const packs = shop?.products.filter((p) => p.type === 'crystals') ?? [];
  const price = (p: ProductPublic) => (provider === 'robokassa' && p.priceRub !== null ? `${p.priceRub} ₽` : `⭐ ${p.priceStars}`);

  const stopPolling = () => { if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; } };
  useEffect(() => () => stopPolling(), []);

  const succeed = (p: ProductPublic) => {
    stopPolling();
    setWaiting(null);
    platform.haptic('success');
    toast(p.type === 'premium' ? 'Premium активирован ✨' : 'Кристаллы начислены ✨');
    setPurchased(true);
    onClose(true);
  };

  /** Опрос /me после ухода на оплату: до 3 минут, каждые 4 с. */
  const startPolling = (p: ProductPublic, before: Me) => {
    let ticks = 0;
    pollTimer.current = window.setInterval(async () => {
      ticks++;
      try {
        const now = await http.get<Me>('/me');
        setMe(now);
        const changed = p.type === 'premium' ? now.premium && now.premiumUntil !== before.premiumUntil : now.wallet.crystals > before.wallet.crystals;
        if (changed) succeed(p);
      } catch { /* сеть — пробуем дальше */ }
      if (ticks > 45) stopPolling();
    }, 4000);
  };

  const buy = async (p: ProductPublic) => {
    if (busy || !me) return;
    setBusy(true);
    try {
      platform.haptic('medium');
      const { invoiceUrl } = await http.post<{ invoiceUrl: string }>('/shop/buy', { kind: 'product', id: p.id, provider });

      if (provider === 'robokassa') {
        setWaiting(p);
        startPolling(p, me);
        if (platform.name === 'telegram') platform.openLink(invoiceUrl); // внешний браузер, мини-апп остаётся открытым
        else window.location.href = invoiceUrl; // вернёт на /?pay=success
        return;
      }

      const res = await platform.pay(invoiceUrl);
      if (typeof res === 'object' && res.dev) {
        const r = await http.post<{ ok: true; user: Me }>('/payments/dev', { productId: res.productId });
        setMe(r.user);
      } else if (res === 'cancelled' || res === 'failed') {
        return;
      } else {
        await new Promise((r) => setTimeout(r, 1500));
        setMe(await http.get<Me>('/me'));
      }
      succeed(p);
    } catch (e) {
      toast((e as Error).message, true);
    } finally {
      setBusy(false);
    }
  };

  const checkNow = async () => {
    if (!waiting || !me) return;
    const now = await http.get<Me>('/me');
    setMe(now);
    const changed = waiting.type === 'premium' ? now.premium : now.wallet.crystals > me.wallet.crystals;
    if (changed) succeed(waiting); else toast('Оплата пока не подтверждена. Обычно это занимает до минуты.');
  };

  return (
    <Sheet open={open} onClose={() => { stopPolling(); onClose(purchased); }}>
      {waiting ? (
        <div className="stack center">
          <h2>Ожидаем оплату</h2>
          <p className="muted">Завершите оплату «{waiting.title}» на открывшейся странице и вернитесь сюда — начисление произойдёт автоматически.</p>
          <div className="spinner" />
          <button className="btn primary" onClick={checkNow}>Я оплатил — проверить</button>
          <button className="btn ghost" onClick={() => { stopPolling(); setWaiting(null); }}>Отмена</button>
        </div>
      ) : (
        <div className="stack">
          <h2>{TITLES[reason]}</h2>
          {reason === 'premium' && me && <p className="muted">Бесплатно — {me.limits.freePerDay} расклада в день. С Premium — без ограничений.</p>}
          {!crystalsOnly && premium && (
            <div className="card lg premium-card">
              <div className="row spread"><h3><Icon name="sparkle" size={18} filled />Premium</h3><span className="pill gold">{price(premium)} / мес</span></div>
              <ul>
                <li>Безлимит раскладов и дополнений</li>
                <li>Расширенная трактовка</li>
                <li>{premium.payload.crystals} кристаллов каждый месяц</li>
                <li>Приоритет в очереди</li>
              </ul>
              <button className="btn warm" disabled={busy} onClick={() => buy(premium)}>Оформить Premium</button>
            </div>
          )}
          {reason === 'append' && me && <p className="dim center">Или разово за {me.config.appendPriceCrystals} кристаллов — купите кристаллы ниже</p>}
          <h3>Кристаллы</h3>
          {packs.map((p) => (
            <div key={p.id} className="card product">
              <div><b><Icon name="crystal" size={18} className="c-crystal" />{p.title}</b>{p.id === 'crystals_550' && <div className="dim">Выгодно</div>}</div>
              <button className="btn ghost sm" disabled={busy} onClick={() => buy(p)}>{price(p)}</button>
            </div>
          ))}
          <p className="dim center">{provider === 'robokassa' ? 'Оплата картой через Robokassa' : 'Оплата через Telegram Stars'}</p>
        </div>
      )}
    </Sheet>
  );
}

/** Хук для удобного вызова пейвола из экранов: `const { paywall, openPaywall } = usePaywall()`. */
export function usePaywall() {
  const [state, setState] = useState<{ reason: PaywallReason; crystalsOnly?: boolean; resolve: (v: boolean) => void } | null>(null);
  const openPaywall = (reason: PaywallReason, crystalsOnly = false) => new Promise<boolean>((resolve) => setState({ reason, crystalsOnly, resolve }));
  const paywall = state ? <Paywall open reason={state.reason} crystalsOnly={state.crystalsOnly} onClose={(v) => { state.resolve(v); setState(null); }} /> : null;
  return { paywall, openPaywall };
}
